import { z } from 'zod';

/**
 * Plan model (Zod) — FINALIZED for M2 (PLAN.md §2a). The plan is the user's whole financial
 * life as a serializable document: it is the API/persistence format AND the fixture format
 * for engine tests. Everything here parses from / serializes to plain JSON.
 *
 * Growth is ADDITIVE, not breaking: enums and optional fields expand, required fields stay
 * stable. M0 shapes are preserved (back-compat) and extended:
 *  - Accounts gain `costBasis?` (foreign-sleeve remittance basis) and an EITHER/OR of a
 *    static `allocation` (asset-class weights) OR a `glidePathRef` (named glide path).
 *  - Income/Expense streams keep `amount`, `growth`, and `start`/`end` as a `TimePoint`
 *    that is EITHER an absolute `{kind:'year'}` OR event-relative `{kind:'event'}`.
 *  - `LifeEvent` is sugar that the simulator desugars into stream start/stops + one-time
 *    flows (see `desugarPlan` notes in BACKEND.md §4).
 *  - `Strategy` carries `contributionOrder` + `withdrawalOrder` (account-id lists) plus an
 *    optional rebalancing band; the runtime Strategy interface lives in ../strategies.
 *
 * Modelled for a Thai investor first: account `kind`s mirror the Thai tax-advantaged
 * vehicles (RMF, SSF, Thai ESG, PVD) and the SET-listed-vs-foreign capital-gains split that
 * the ThailandTaxPolicy keys off (see ../tax/th).
 */

// ---------------------------------------------------------------------------
// Household
// ---------------------------------------------------------------------------

/** A person in the household. `birthYear` drives retirement age, the plan horizon, and Thai
 *  RMF eligibility (age 55) downstream. */
export const PersonSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    birthYear: z.number().int(),
  })
  .strict();
export type Person = z.infer<typeof PersonSchema>;

export const HouseholdSchema = z
  .object({
    people: z.array(PersonSchema).min(1),
  })
  .strict();
export type Household = z.infer<typeof HouseholdSchema>;

// ---------------------------------------------------------------------------
// Timing — absolute years and event-relative anchors
// ---------------------------------------------------------------------------

/**
 * A point in time for a stream boundary or event anchor. Either an absolute calendar year,
 * or anchored to a named life event (e.g. "until retirement"). Event-relative timing is
 * resolved against the plan's events when the simulator desugars the plan
 * (`resolveTimePoint` — BACKEND.md §4). A cycle/missing-event reference is a plan error.
 *
 * Field names: the event variant uses `eventId` + `offsetYears` (back-compat with M0). The
 * PLAN prose shorthand "{kind:'event', event, offset}" maps onto these names.
 */
export const TimePointSchema = z.union([
  z.object({ kind: z.literal('year'), year: z.number().int() }).strict(),
  z
    .object({
      kind: z.literal('event'),
      eventId: z.string().min(1),
      /** Offset in years from the event (default 0; negative = before the event). */
      offsetYears: z.number().int().default(0),
    })
    .strict(),
]);
export type TimePoint = z.infer<typeof TimePointSchema>;

// ---------------------------------------------------------------------------
// Stream growth — fixed-rate (default) | realized-CPI-indexed (ENRICHMENT)
// ---------------------------------------------------------------------------

/**
 * A stream's growth/indexing mode (ENRICHMENT, PLAN-MODEL pass — resolves the M3 realized-
 * inflation-indexing TODO). A DISCRIMINATED UNION so streams can index off the YEAR'S REALIZED
 * inflation rather than a hard-coded nominal rate:
 *
 *  - `{kind:'fixed', rate}`  — the CURRENT behaviour and the DEFAULT. `amount` compounds at a
 *    constant nominal `rate` per year: `amount · (1+rate)^(year−startYear)`. Back-compat: a bare
 *    `growth: number` on a stream is normalised to `{kind:'fixed', rate}` by the simulator's
 *    desugar step, so every existing plan/fixture/test is byte-identical.
 *  - `{kind:'cpi', realRate?}` — index by the SIMULATED year's REALIZED inflation (carried on the
 *    `ReturnInput`/`ReturnData.inflation` column), optionally PLUS a constant real drift `realRate`
 *    (default 0). The per-year multiplier compounds `(1+cpi_y)·(1+realRate)` across the simulated
 *    years from the stream's start to `year` (so a `cpi` stream with `realRate:0` holds constant
 *    PURCHASING POWER). When the run carries no inflation column, a `cpi` stream is treated as
 *    flat (cpi=0) — a documented, visible fallback, never a silent throw.
 *
 * NOTE on placement: the union lives on the stream as an OPTIONAL `growthMode` field (below),
 * mutually exclusive with the legacy scalar `growth`. The scalar is retained as the back-compat
 * default; `growthMode` (when present) overrides it. This keeps the M0 `StreamBase` shape — and
 * therefore the M6 plan CRUD + every committed fixture — valid unchanged.
 */
export const StreamGrowthSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('fixed'),
      /** Constant nominal growth rate per year (decimal, e.g. 0.03). Default 0. */
      rate: z.number().default(0),
    })
    .strict(),
  z
    .object({
      kind: z.literal('cpi'),
      /** Optional constant REAL drift on top of realized CPI (decimal). Default 0 = hold real
       *  purchasing power. */
      realRate: z.number().default(0),
    })
    .strict(),
]);
export type StreamGrowth = z.infer<typeof StreamGrowthSchema>;

// ---------------------------------------------------------------------------
// Allocation & glide paths
// ---------------------------------------------------------------------------

/**
 * Asset allocation as weights keyed by asset-class id. Keys line up with `@projections/data`
 * series ids / asset classes (e.g. `th_set_tr`, `global_acwi_tr`, `cash`). Weights are
 * non-negative; the simulator normalises them (does NOT require an exact sum of 1), so a
 * `{ eq: 60, bond: 40 }` and a `{ eq: 0.6, bond: 0.4 }` describe the same mix.
 */
export const AllocationSchema = z.record(z.string(), z.number().nonnegative());
export type Allocation = z.infer<typeof AllocationSchema>;

/**
 * A named glide path: a schedule of allocations that shift over time (e.g. de-risking
 * toward retirement). Each stop is an allocation effective from a `TimePoint`; the simulator
 * holds the most-recent stop's allocation until the next stop. Glide paths are referenced by
 * id from an account's `glidePathRef`, so several accounts can share one.
 *
 * M2 carries the SHAPE and resolution rule; an account may instead pin a static
 * `allocation`. (The deterministic core in M2 applies a single portfolio-level return, so
 * allocation/glide-path weights drive per-asset returns once `ReturnInput` is per-asset —
 * the contract supports both today.)
 */
export const GlidePathStopSchema = z
  .object({
    at: TimePointSchema,
    allocation: AllocationSchema,
  })
  .strict();
export type GlidePathStop = z.infer<typeof GlidePathStopSchema>;

export const GlidePathSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    /** Stops in chronological order; the first stop's allocation applies before it too. */
    stops: z.array(GlidePathStopSchema).min(1),
  })
  .strict();
export type GlidePath = z.infer<typeof GlidePathSchema>;

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

/**
 * Account kinds. The split is tax-treatment-driven (PLAN.md §2a):
 * - `taxable_set`     SET-listed equities — capital gains EXEMPT for individuals (Thailand).
 * - `taxable_foreign` foreign sleeve — gains relevant via the remittance rule (Por.161/162);
 *                     cost basis matters here (Thailand).
 * - `rmf`             Retirement Mutual Fund — deduction up to cap, hold to age 55 & >=5y (Thailand).
 * - `ssf`             Super Savings Fund (Thailand).
 * - `thai_esg`        Thai ESG / Thai ESGX (Thailand).
 * - `pvd`             Provident fund (employer match) (Thailand).
 * - `cash`            Bank deposit / money-market (jurisdiction-agnostic).
 *
 * UNITED STATES (M8 — additive, proves the TaxPolicy plug; the Thai policy never reads these and the
 * M4 goldens are unaffected):
 * - `taxable_us`      US taxable brokerage — realised LONG-TERM capital gains taxed at 0/15/20%.
 * - `traditional`     Traditional (pre-tax) 401(k)/IRA — withdrawals are ORDINARY income; RMDs from
 *                     age 73; 10% penalty before age 59½.
 * - `roth`            Roth 401(k)/IRA — qualified withdrawals TAX-FREE; no lifetime RMD.
 */
export const AccountKindSchema = z.enum([
  'taxable_set',
  'taxable_foreign',
  'rmf',
  'ssf',
  'thai_esg',
  'pvd',
  'cash',
  // --- United States (M8) ---
  'taxable_us',
  'traditional',
  'roth',
]);
export type AccountKind = z.infer<typeof AccountKindSchema>;

/**
 * An account. Each holds a `balance`, an optional `costBasis` (foreign-sleeve remittance
 * basis; irrelevant for SET-listed gains which are exempt), and EXACTLY ONE of:
 *  - `allocation`: static asset-class weights, OR
 *  - `glidePathRef`: the id of a `GlidePath` on the plan.
 * Validation enforces the either/or so an account always has a resolvable allocation.
 */
export const AccountSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    kind: AccountKindSchema,
    /** Current balance in plan currency (THB by convention). */
    balance: z.number(),
    /**
     * Cost basis — the BOOK VALUE of the holding (what was paid for the current `balance`). The
     * unrealised gain is `balance − costBasis`. Relevant for the foreign sleeve (remittance rule)
     * and now, with the ENRICHMENT income-vs-gain split, for ANY taxable account: a withdrawal
     * realises a proportional slice of the embedded gain (average-basis accounting — see
     * `core/simulate`). OPTIONAL with a BACK-COMPAT default: when omitted, the account is treated
     * as having cost basis EQUAL to its balance (zero embedded gain), so a plan that never set
     * `costBasis` realises ZERO capital gains on withdrawal — byte-identical to pre-enrichment
     * behaviour. Irrelevant for `cash`, and for SET-listed gains which are tax-exempt regardless
     * (the split still TRACKS the SET-listed realised gain so `explain` shows it; the policy
     * exempts it).
     */
    costBasis: z.number().optional(),
    /**
     * Dividend YIELD assumption for this account's equity-like holdings (decimal, e.g. 0.03 = 3%
     * of balance accrues as dividends each year), the ENRICHMENT dividend-accrual input. OPTIONAL,
     * DEFAULT 0 (back-compat: a plan that never sets it accrues no dividends, so dividends fed to
     * the tax policy stay 0 exactly as before). Applied to the account's START-of-year balance
     * each simulated year and fed (summed across accounts) into `YearTaxInput.dividends`. A
     * per-asset-class default may layer in later (see `Plan.assumptions`); the per-account value
     * always wins when set.
     */
    dividendYield: z.number().nonnegative().optional(),
    /** Static asset-class weights. Mutually exclusive with `glidePathRef`. */
    allocation: AllocationSchema.optional(),
    /** Reference to a named `GlidePath` on the plan. Mutually exclusive with `allocation`. */
    glidePathRef: z.string().min(1).optional(),
  })
  .strict()
  .refine((a) => (a.allocation === undefined) !== (a.glidePathRef === undefined), {
    message: 'Account must have exactly one of `allocation` or `glidePathRef`',
    path: ['allocation'],
  });
export type Account = z.infer<typeof AccountSchema>;

// ---------------------------------------------------------------------------
// Income & expense streams
// ---------------------------------------------------------------------------

const StreamBase = {
  id: z.string().min(1),
  name: z.string().min(1),
  /** Annual amount in plan currency at `start`. */
  amount: z.number(),
  /**
   * LEGACY scalar growth/indexing rate (decimal, e.g. 0.03). The BACK-COMPAT default. Used as
   * `{kind:'fixed', rate: growth}` UNLESS `growthMode` is set (which overrides it). Existing
   * plans/fixtures/tests carry only this field, so they keep `fixed`-rate semantics unchanged.
   */
  growth: z.number().default(0),
  /**
   * ENRICHMENT (optional): an explicit growth mode (`fixed` | `cpi`). When PRESENT it OVERRIDES
   * the scalar `growth`; when ABSENT the simulator desugars `growth` into `{kind:'fixed', rate:
   * growth}`. This is how a stream opts into REALIZED-CPI indexing (`{kind:'cpi'}`) without
   * breaking the M0 `growth`-only shape every fixture relies on.
   */
  growthMode: StreamGrowthSchema.optional(),
  start: TimePointSchema,
  /** Omit for "runs to the plan horizon". */
  end: TimePointSchema.optional(),
};

export const IncomeStreamSchema = z
  .object({
    ...StreamBase,
    /** Whether this income is assessable for Thai PIT — feeds YearTaxInput (M4). */
    assessable: z.boolean().default(true),
  })
  .strict();
export type IncomeStream = z.infer<typeof IncomeStreamSchema>;

/** Coarse spend category, used only for ledger drill-down grouping (not tax). */
export const ExpenseCategorySchema = z.enum([
  'living',
  'housing',
  'education',
  'healthcare',
  'discretionary',
  'other',
]);
export type ExpenseCategory = z.infer<typeof ExpenseCategorySchema>;

export const ExpenseStreamSchema = z
  .object({
    ...StreamBase,
    /** Drill-down grouping only; defaults to `other`. */
    category: ExpenseCategorySchema.default('other'),
  })
  .strict();
export type ExpenseStream = z.infer<typeof ExpenseStreamSchema>;

// ---------------------------------------------------------------------------
// Life events
// ---------------------------------------------------------------------------

/**
 * Life events are SUGAR that the simulator desugars (BACKEND.md §4) into stream start/stops
 * (via event-relative `TimePoint`s) and one-time flows. They are also valid anchors for any
 * `TimePoint` of `kind:'event'`.
 *
 *  - `retirement`     anchors income stops / expense changes; drives strategy phase.
 *  - `house_purchase` a one-time outflow (`oneTimeFlow` negative) + optional ongoing expense.
 *  - `education`      a bounded expense window for a child.
 *  - `custom`         a bare anchor + optional one-time flow.
 */
export const LifeEventKindSchema = z.enum(['retirement', 'house_purchase', 'education', 'custom']);
export type LifeEventKind = z.infer<typeof LifeEventKindSchema>;

export const LifeEventSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    kind: LifeEventKindSchema,
    at: TimePointSchema,
    /** Optional one-time cash flow at the event (negative = outflow, e.g. a house deposit). */
    oneTimeFlow: z.number().optional(),
    /** Optional id of the household person this event is about (e.g. whose retirement). */
    personId: z.string().min(1).optional(),
  })
  .strict();
export type LifeEvent = z.infer<typeof LifeEventSchema>;

// ---------------------------------------------------------------------------
// Strategy — contribution & withdrawal ordering
// ---------------------------------------------------------------------------

/**
 * Plan-level ordering config. Both lists are arrays of account ids. The simulator funnels
 * each year's surplus into accounts in `contributionOrder` and draws each year's shortfall
 * from accounts in `withdrawalOrder`. An empty list means "natural account order". Real Thai
 * ordering logic (PVD match -> RMF/ESG to cap -> taxable; taxable -> RMF after 55 -> ...)
 * lives in ../strategies and is wired in M4; this is the serialized declaration of intent.
 */
export const StrategySchema = z
  .object({
    contributionOrder: z.array(z.string()).default([]),
    withdrawalOrder: z.array(z.string()).default([]),
    /** Annual rebalancing band as a decimal drift tolerance (0 = rebalance every year to
     *  target; omit = no rebalancing). Carried for M2; applied once returns are per-asset. */
    rebalanceBand: z.number().nonnegative().optional(),
  })
  .strict();
export type Strategy = z.infer<typeof StrategySchema>;

// ---------------------------------------------------------------------------
// Plan-level assumptions (ENRICHMENT) — per-asset-class dividend-yield defaults
// ---------------------------------------------------------------------------

/**
 * Plan-wide modelling assumptions (ENRICHMENT, all OPTIONAL / back-compat). Today it carries the
 * per-ASSET-CLASS dividend-yield defaults: a map `assetClassId -> yield (decimal)`. When an
 * account does NOT set its own `dividendYield`, the simulator derives one from this map by
 * allocation-weighting the per-class yields over the account's effective allocation
 * (`Σ_a (w_a/Σw)·dividendYields[assetClasses[a]]`, missing keys → 0). When NEITHER is present the
 * yield is 0 (full back-compat). An account's explicit `dividendYield` always wins.
 */
export const PlanAssumptionsSchema = z
  .object({
    /** Default dividend yield per asset-class id (decimal). Missing keys → 0. */
    dividendYields: z.record(z.string(), z.number().nonnegative()).optional(),
  })
  .strict();
export type PlanAssumptions = z.infer<typeof PlanAssumptionsSchema>;

// ---------------------------------------------------------------------------
// Plan
// ---------------------------------------------------------------------------

export const PlanSchema = z
  .object({
    /** Schema/document version — lets persistence migrate plans across milestones. */
    version: z.literal(1).default(1),
    household: HouseholdSchema,
    accounts: z.array(AccountSchema),
    incomes: z.array(IncomeStreamSchema),
    expenses: z.array(ExpenseStreamSchema),
    events: z.array(LifeEventSchema),
    /** Named glide paths referenced by accounts' `glidePathRef`. */
    glidePaths: z.array(GlidePathSchema).default([]),
    strategy: StrategySchema,
    /** First simulated calendar year (inclusive). */
    startYear: z.number().int(),
    /** Number of years to project; the ledger has one row per year. */
    horizonYears: z.number().int().positive(),
    /** Optional plan-wide inflation rate (decimal) for real-vs-nominal reporting; the core
     *  is currency/inflation-agnostic, this is metadata for results. */
    inflation: z.number().optional(),
    /** ENRICHMENT (optional): plan-wide modelling assumptions (per-asset-class dividend-yield
     *  defaults). Omitted → no defaults (full back-compat). */
    assumptions: PlanAssumptionsSchema.optional(),
  })
  .strict()
  .superRefine((plan, ctx) => {
    // glidePathRefs must resolve to a declared glide path.
    const glideIds = new Set(plan.glidePaths.map((g) => g.id));
    for (const acct of plan.accounts) {
      if (acct.glidePathRef !== undefined && !glideIds.has(acct.glidePathRef)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Account "${acct.id}" references unknown glidePathRef "${acct.glidePathRef}"`,
          path: ['accounts'],
        });
      }
    }
    // event-relative TimePoints must reference a declared event.
    const eventIds = new Set(plan.events.map((e) => e.id));
    const checkPoint = (tp: TimePoint | undefined, where: string): void => {
      if (tp !== undefined && tp.kind === 'event' && !eventIds.has(tp.eventId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${where} references unknown eventId "${tp.eventId}"`,
          path: ['events'],
        });
      }
    };
    for (const s of plan.incomes) {
      checkPoint(s.start, `Income "${s.id}" start`);
      checkPoint(s.end, `Income "${s.id}" end`);
    }
    for (const s of plan.expenses) {
      checkPoint(s.start, `Expense "${s.id}" start`);
      checkPoint(s.end, `Expense "${s.id}" end`);
    }
    for (const e of plan.events) {
      checkPoint(e.at, `Event "${e.id}" at`);
    }
  });
export type Plan = z.infer<typeof PlanSchema>;

/** Parse + validate an unknown value into a Plan (throws on invalid). */
export function parsePlan(input: unknown): Plan {
  return PlanSchema.parse(input);
}

/** Safe-parse variant returning Zod's success/error result (for API boundaries). */
export function safeParsePlan(input: unknown): ReturnType<typeof PlanSchema.safeParse> {
  return PlanSchema.safeParse(input);
}
